import type { LotView, Stance } from "@hammer/core";
import { formatMoney } from "@hammer/core/money";
import { formatAge, formatTimeLeft } from "@hammer/core/time";
import { useEffect, useRef, useState } from "react";
import { useNow } from "../lib/store.ts";

const SOURCE_LABELS: Record<string, string> = {
  hibid: "HiBid", asw: "Auction house", ctbids: "CTBids", publicsurplus: "Public Surplus", auctionninja: "AuctionNinja",
  proxibid: "Proxibid", liveauctioneers: "LiveAuctioneers", govdeals: "GovDeals", allsurplus: "AllSurplus", bidspotter: "BidSpotter", generic: "",
};

export function sourceLabel(l: Pick<LotView, "source" | "url" | "seller">): string {
  if (l.source === "asw" && l.seller) return l.seller;
  return SOURCE_LABELS[l.source] || new URL(l.url).hostname.replace(/^www\./, "");
}

/** The price, printed as honestly as its age allows. */
export function Price({ lot, size = "row" }: { lot: LotView; size?: "row" | "big" }) {
  const now = useNow();
  const closed = lot.phase === "closed";
  const sold = lot.result?.hammerCents ?? lot.latest?.soldPriceCents ?? null;
  const cents = closed ? (sold ?? lot.latest?.currentBidCents ?? null) : (lot.latest?.currentBidCents ?? null);
  // Red is for when an old price matters: you're bidding and it closes within a day. Otherwise old is just faded.
  const closesSoon = !!lot.latest?.endAt && Date.parse(lot.latest.endAt) - now < 86_400_000;
  const raw = closed ? "fresh" : lot.freshness.level;
  const level = raw === "failing" && !(lot.stance.stance === "bidding" && closesSoon) ? "stale" : raw;
  const age = lot.freshness.lastOkAt ? now - Date.parse(lot.freshness.lastOkAt) : null;
  const flash = useFlashOnChange(cents);

  let note: string | null = null;
  if (closed) note = lot.latest?.sourceState === "unsold" ? "no sale" : sold != null ? "sold" : "closed";
  else if (!lot.latest || lot.freshness.level === "none") note = "not checked yet";
  else if (level === "stale") note = `as of ${formatAge(age)} ago${lot.reach === "capture" ? ", use the bookmarklet" : ""}`;
  else if (level === "failing") note = `can't check, ${formatAge(age)} old`;
  else {
    // Prices refresh once a day, so even a current one says how old it is.
    const bids = lot.latest.bidCount && cents != null ? `${lot.latest.bidCount} bid${lot.latest.bidCount === 1 ? "" : "s"}, ` : "";
    note = `${bids}${formatAge(age) === "just now" ? "just now" : `${formatAge(age)} ago`}`;
  }

  return (
    <span className={`price ${flash ? "price-flash" : ""}`} data-fresh={level} title={lot.freshness.lastOkAt ? `Checked ${new Date(lot.freshness.lastOkAt).toLocaleString()}` : "Never checked"}>
      <span className={`price-n board ${size === "big" ? "text-[31px]" : "text-[18px]"}`}>{cents == null ? (closed ? "—" : level === "fresh" ? "No bids" : "Unknown") : formatMoney(cents, lot.currency)}</span>
      {note && <span className="price-asof">{note}</span>}
    </span>
  );
}

function useFlashOnChange(v: unknown) {
  const prev = useRef(v);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (prev.current !== v && prev.current !== undefined) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 1700);
      prev.current = v;
      return () => clearTimeout(t);
    }
    prev.current = v;
  }, [v]);
  return flash;
}

const dayFmt = new Intl.DateTimeFormat(undefined, { weekday: "short", hour: "numeric", minute: "2-digit" });
const dateFmt = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function absoluteTime(iso: string, now: number) {
  const d = new Date(iso);
  return Math.abs(d.getTime() - now) < 6 * 86_400_000 ? dayFmt.format(d) : dateFmt.format(d);
}

/** Countdown plus the wall-clock time it ends. */
export function TimeLeft({ lot, size = "row" }: { lot: LotView; size?: "row" | "big" }) {
  const now = useNow();
  const end = lot.latest?.endAt ?? null;
  const cls = `board ${size === "big" ? "text-[31px]" : "text-[18px]"}`;
  if (lot.phase === "closed") {
    return (
      <span className="left" data-urgency="closed">
        <span className={cls}>Ended</span>
        {end && <span className="price-asof">{absoluteTime(end, now)}</span>}
      </span>
    );
  }
  if (!end) return <span className="left faint"><span className={cls}>—</span><span className="price-asof">no end time</span></span>;
  const ms = Date.parse(end) - now;
  const urgency = ms <= 0 ? "overtime" : ms <= 3_600_000 ? "final" : ms > 7 * 86_400_000 ? "far" : "normal";
  const extra = lot.endKind === "soft_close" ? ", may extend" : lot.endKind === "live_sequence" ? ", live sale" : "";
  return (
    <span className="left" data-urgency={urgency}>
      <span className={cls}>{ms <= 0 ? "Ended" : formatTimeLeft(ms)}</span>
      <span className="price-asof">{absoluteTime(end, now)}{extra}</span>
    </span>
  );
}

const STANCE_LABELS: [Stance, string, string][] = [
  ["watching", "Watch", "w"],
  ["bidding", "Bid", "b"],
  ["passed", "Pass", "p"],
];

export function StanceControl({ value, onChange, disabled }: { value: Stance; onChange: (s: Stance) => void; disabled?: boolean }) {
  return (
    <div className="stance" role="group" aria-label="Your plan for this lot">
      {STANCE_LABELS.map(([v, label, key]) => (
        <button key={v} type="button" data-v={v} aria-pressed={value === v} disabled={disabled} onClick={() => onChange(v)} title={`${label} (${key})`}>
          {label}
        </button>
      ))}
    </div>
  );
}

export function Thumb({ src, size = "row" }: { src?: string; size?: "row" | "big" }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <div className={`thumb thumb-empty ${size === "big" ? "!w-full !h-auto aspect-[4/3]" : ""}`}>No photo</div>;
  return <img className={`thumb ${size === "big" ? "!w-full !h-auto aspect-[4/3]" : ""}`} src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
}

const ISSUE_FLAG: Partial<Record<string, [string, "warn" | "bad"]>> = {
  fetch_failing: ["Can't check", "bad"],
  stale: ["Needs a refresh", "warn"],
  record_result: ["Record result", "warn"],
  low_confidence_parse: ["Check details", "warn"],
  suspicious_value: ["Check details", "warn"],
  end_time_past: ["Check end date", "warn"],
};

export function LotFlags({ lot, withTags = true }: { lot: LotView; withTags?: boolean }) {
  const seen = new Set<string>();
  const flags = lot.openIssues
    .map((i) => ISSUE_FLAG[i.kind])
    .filter((f): f is [string, "warn" | "bad"] => !!f && !seen.has(f[0]) && !!seen.add(f[0]));
  const over = lot.stance.stance === "bidding" && lot.stance.maxBidCents != null && (lot.latest?.currentBidCents ?? 0) > lot.stance.maxBidCents;
  if (!flags.length && !over && !(withTags && lot.tags.length)) return null;
  return (
    <div className="r-flags">
      {over && <span className="flag flag-bad">Over your max</span>}
      {flags.map(([t, tone]) => <span key={t} className={`flag flag-${tone}`}>{t}</span>)}
      {withTags && lot.tags.map((t) => <span key={t} className="flag flag-tag">{t}</span>)}
    </div>
  );
}
